import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";

import { DownloadAndPrint } from "../../components/DownloadAndPrint";
import { get_date } from "../../components/generalComponets";
import { CardLayout, Invoice, PageHeader } from "../../components/v3";
import { PageLayout } from "../../layouts/PageLayout";
import { PurchaseService } from "../../services";
import { getDateAndTime } from "../../utils/formatters";

interface UserInfo {
  firstName: string;
  lastName: string;
}

interface ReturnItem {
  purchaseItemId: string;
  price: string | number;
  quantity: string | number;
  taxPercentage?: string | number;
  totalAmount: string | number;
}

interface PurchaseReturnData {
  items?: ReturnItem[];
  purchaseReturnNumber?: string;
  returnDate?: string;
  purchaseInvoice?: {
    purchaseInvoiceNumber: string;
  };
  createdBy?: UserInfo;
  createdAt?: string;
  updatedBy?: UserInfo;
  updatedAt?: string;
  company?: { name: string };
  vendor?: { name: string; primaryPhone?: string };
}

export const PurchaseReturnInvoice = () => {
  const [returnData, setReturnData] = useState<Partial<PurchaseReturnData>>({ items: [] });
  const [itemsMaster, setItemsMaster] = useState([]);

  const subtotalOfTotalReturnItems = Number(
    (returnData?.items || []).reduce((acc, curr) => acc + Number(curr?.totalAmount || 0), 0).toFixed(2),
  );

  const printRef = useRef<HTMLDivElement>(null);

  const { returnId } = useParams();

  useEffect(() => {
    fetchData(returnId);
  }, [returnId]);

  const fetchData = async (id) => {
    try {
      const { data: response } = await PurchaseService.getPurchaseReturnById(id);

      setReturnData(response);

      const itemIds = Array.from(new Set(response?.items?.map((i) => i.purchaseItemId)));

      if (itemIds.length) {
        const { data: purchaseItems } = await PurchaseService.getPurchaseItems({
          ids: itemIds.join(","),
        });

        setItemsMaster(purchaseItems?.rows || []);
      }
    } catch (error) {
      toast.error("Failed to fetch return invoice data");
      console.error("Error fetching return invoice:", error);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });

  /* ============================================
     TABLE ROW MAPPING
  ============================================ */

  const rows =
    returnData?.items?.map((item, index) => {
      const material = itemsMaster.find((i) => i.id === item.purchaseItemId);

      return {
        sl: index + 1,
        item: material?.name || "N/A",
        rate: item?.price || "-",
        quantity: item?.quantity || "-",
        tax: item?.taxPercentage || "-",
        total: item?.totalAmount || "-",
      };
    }) || [];

  return (
    <PageLayout>
      {/* HEADER */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <PageHeader title="Purchase Return Invoice" />
      </Box>

      {/* INVOICE BODY */}
      <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
        <CardLayout>
          <div ref={printRef}>
            <Invoice
              title="Purchase Return Invoice"
              sectionA={[
                {
                  label: "Return No",
                  value: returnData?.purchaseReturnNumber,
                },
                {
                  label: "Return Date",
                  value: get_date(returnData?.returnDate, "DD-MM-YYYY"),
                },
                {
                  label: "Original Invoice No",
                  value: returnData?.purchaseInvoice?.purchaseInvoiceNumber,
                },
              ]}
              sectionB={[
                {
                  label: "Created By",
                  value: returnData?.createdBy
                    ? `${returnData?.createdBy?.firstName} ${returnData?.createdBy?.lastName} (${getDateAndTime(
                        returnData?.createdAt,
                      )})`
                    : "N/A",
                },
                returnData?.updatedBy && {
                  label: "Last Updated By",
                  value: `${returnData?.updatedBy?.firstName ?? ""} ${returnData?.updatedBy?.lastName ?? ""} (${getDateAndTime(
                    returnData?.updatedAt,
                  )})`,
                },
              ].filter(Boolean)}
              sectionC={[
                {
                  label: returnData?.company?.name,
                  value: "",
                },
              ]}
              sectionD={[
                {
                  label: "Vendor",
                  value: (
                    <>
                      {returnData?.vendor?.name}
                      <br />
                      {returnData?.vendor?.primaryPhone}
                    </>
                  ),
                },
              ]}
              columns={[
                { id: "sl", label: "Sl No" },
                { id: "item", label: "Item" },
                { id: "rate", label: "Rate" },
                { id: "quantity", label: "Return Qty" },
                { id: "tax", label: "Tax" },
                { id: "total", label: "Total" },
              ]}
              rows={rows}
              subtotal={Number(subtotalOfTotalReturnItems).toFixed(2)}
            />
          </div>
        </CardLayout>
      </Box>

      <DownloadAndPrint handlePrint={handlePrint} printRef={printRef} fileName={"PurchaseReturnInvoice.pdf"} />
    </PageLayout>
  );
};
